import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../../app/appTypes";
import { getWithAuth, putWithAuth, patchWithAuth } from "../../../http";
import { MarketRegionEnum } from "./marketRegion";
import { CertificationStandardEnum } from "./companyCertificates";
import { CompanyConsentTypeEnum } from "./enums/companyConsentEnum";
import { SocialPlatformEnum } from "./enums/socialPlatformEnum";
import { QuickTagEnum } from "./enums/QuickTagEnum";
import { ReactNode } from "react";

export enum CompanySizeTier {
    Employees_1_9 = 1,
    Employees_10_49 = 2,
    Employees_50_99 = 3,
    Employees_100_249 = 4,
    Employees_250_499 = 5,
    Employees_500_999 = 6,
    Employees_1000_4999 = 7,
    Employees_5000_9999 = 8,
    Employees_10000_24999 = 9,
    Employees_25000_49999 = 10,
    Employees_50000_Plus = 11,
}

export interface CompanyDescriptionPayload {
    fieldName: string
    text: ReactNode
}

export interface CompanyMarketplaceConsent {
    id: number;
    isMarketPlaceActive: boolean;
    restrictVisibilityToBuyers: boolean;
    consents: CompanyConsent[];
}

export interface CompanyConsent {
    id: number;
    granted: boolean;
    lastChangedAt: Date;
    lastChangedBy: string;
    type: CompanyConsentTypeEnum;
    typeId: number;
}

export interface CompanyMarket {
    id: number;
    companyProfileId: number;
    region: MarketRegionEnum;
    regionId: number;
}

export interface CompanyCountryMarket {
    id: number;
    companyProfileId: number;
    countryCode: string;
    countryName: string;
}

export interface CompanyQuickTag {
    id: number;
    companyProfileId: number;
    quickTagId: number;
    quickTag: QuickTagEnum;
}

export interface CompanyCertificates {
    id: number;
    companyProfileId: number;
    certificateId: number;
    certificate: CertificationStandardEnum;
}

export interface CompanySocialLink {
    id: number;
    companyProfileId: number;
    socialPlatformId: number;
    socialPlatform: SocialPlatformEnum;
    url: string | null;
    isPrimary: boolean;
}

export interface CompanyProfile {
    id: number;
    companyId: number;
    companySizeTierId: string;
    shortName: string | null;
    legalName: string | null;
    about: string | null;
    yearFounded: number | null;
    timeZone: string | null;
    isSupplier: boolean;
    guid: string;
    logoUrl: string | null;

    websiteUrl: string | null;
    primaryEmail: string | null;
    salesEmail: string | null;
    supportEmail: string | null;
    phone: string | null;
    whatsApp: string | null;

    // dokładnie jak w encji
    hQ_Street: string | null;
    hQ_BuildingNumber: string | null;
    hQ_UnitNumber: string | null;
    hQ_AddressAdditional: string | null;
    hQ_City: string | null;
    hQ_Region: string | null;
    hQ_RegionCode: string | null;
    hQ_PostalCode: string | null;
    hQ_CountryCode: string | null;
    hQ_CountryName: string | null;

    hQ_Latitude: number | null;
    hQ_Longitude: number | null;
    isMarketPlaceActive: boolean;
    restrictVisibilityToBuyers: boolean;
    // markets: CompanyProfileMarket[];
    logoFileId: string;
    consents: CompanyConsent[];
    markets: CompanyMarket[];
    countryMarkets: CompanyCountryMarket[];
    certificates: CompanyCertificates[];
    socialLinks: CompanySocialLink[];
    quickTags: CompanyQuickTag[]
}

export const loadCompanyProfile = createAsyncThunk<CompanyProfile, void, AppThunkAPIType>(
    "companyProfile/load",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/company-profile` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const updateCompanyProfile = createAsyncThunk<CompanyProfile, CompanyProfile & { id: string }, AppThunkAPIType>(
    "companies/update", async (company, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/company-profile/${company.id}`,
            payload: company,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateCompanyProfileDescription = createAsyncThunk<CompanyProfile, { id: number, data: CompanyDescriptionPayload }, AppThunkAPIType>(
    "companyProfile/update-description",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/company-profile/${id}/description`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateMarkets = createAsyncThunk<CompanyProfile, { id: number, markets: CompanyMarket[] }, AppThunkAPIType>(
    "companyProfile/update-markets",
    async ({ id, markets }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/markets`,
            payload: markets,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateCountryMarkets = createAsyncThunk<CompanyProfile, { id: number, countryMarkets: CompanyCountryMarket[] }, AppThunkAPIType>(
    "companyProfile/update-country-markets",
    async ({ id, countryMarkets }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/country-markets`,
            payload: countryMarkets,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateQuickTags = createAsyncThunk<CompanyProfile, { id: number, quickTags: CompanyQuickTag[] }, AppThunkAPIType>(
    "companyProfile/update-quick-tags",
    async ({ id, quickTags }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/quick-tags`,
            payload: quickTags,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateSocialLinks = createAsyncThunk<CompanyProfile, { id: number, socialLinks: CompanySocialLink[] }, AppThunkAPIType>(
    "companyProfile/update-social-links",
    async ({ id, socialLinks }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/social-links`,
            payload: socialLinks,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateCertificates = createAsyncThunk<CompanyProfile, { id: number, certificates: CompanyCertificates[] }, AppThunkAPIType>(
    "companyProfile/update-certificates",
    async ({ id, certificates }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/certificates`,
            payload: certificates,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateMarketplaceConsents = createAsyncThunk<CompanyProfile, { id: number, marketplaceConsents: CompanyMarketplaceConsent }, AppThunkAPIType>(
    "companyProfile/update-marketplace-consents",
    async ({ id, marketplaceConsents }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(patchWithAuth({
            url: `api/company-profile/${id}/marketplace-consents`,
            payload: marketplaceConsents,
        }))
        const { payload } = result
        if (patchWithAuth.fulfilled.match(result)) {
            return payload as CompanyProfile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateCompanyLogo = createAsyncThunk<{ id: number, url: string }, { id: number, url: string }, AppThunkAPIType>(
    "companies/update-logo", async (data, { dispatch, rejectWithValue }) => {
        const { url } = data
        const result = await dispatch(putWithAuth({
            url: `api/company-profile/company-logo-url/${data.id}`,
            payload: { url },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return data
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)
